import React, { useState, useEffect } from "react";
import { X, UserPlus, ArrowLeft, Check, Mail, Eye, EyeOff, Loader2, AlertTriangle, KeyRound, Settings2 } from "lucide-react";
import { toast } from "sonner";
import * as api from "../api";
import SocialAccountForm from "./SocialAccountForm";

const PROVIDERS = [
  { id: "google", nome: "Google / Gmail", lettera: "G", colore: "#ea4335" },
  { id: "libero", nome: "Libero", lettera: "L", colore: "#0057a3" },
  { id: "arubamail", nome: "Aruba Mail", lettera: "A", colore: "#f25c05" },
  { id: "aruba", nome: "Aruba PEC", lettera: "A", colore: "#d94000" },
  { id: "legalmail", nome: "PEC Legalmail", lettera: "P", colore: "#1a3c6e" },
  { id: "namirial", nome: "PEC Namirial", lettera: "N", colore: "#00857c" },
  { id: "outlook", nome: "Outlook", lettera: "O", colore: "#0072c6" },
  { id: "yahoo", nome: "Yahoo", lettera: "Y", colore: "#6001d2" },
  { id: "virgilio", nome: "Virgilio", lettera: "V", colore: "#e57200" },
  { id: "tiscali", nome: "Tiscali", lettera: "T", colore: "#c40000" },
  { id: "altro", nome: "Altro (IMAP / POP3)", lettera: "@", colore: "#122a44" },
  { id: "facebook", nome: "Facebook Pagina (Messenger)", lettera: "f", colore: "#1877f2", social: true, rete: "facebook" },
];

export default function AddAccountModal({ open, onClose, onAdd, preselect }) {
  const [provider, setProvider] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mostraPassword, setMostraPassword] = useState(false);
  const [isPec, setIsPec] = useState(false);
  const [nome, setNome] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [errore, setErrore] = useState("");
  const [manuale, setManuale] = useState(false);
  const [protocollo, setProtocollo] = useState("imap");
  const [inHost, setInHost] = useState("");
  const [inPort, setInPort] = useState("");
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState("");

  const dominio = email.includes("@") ? email.split("@")[1].trim().toLowerCase() : "";
  const suggeriti = {
    inHost: (protocollo === "pop3" ? "pop." : "imap.") + (dominio || "dominio.it"),
    inPort: protocollo === "pop3" ? "995" : "993",
    smtpHost: "smtp." + (dominio || "dominio.it"),
    smtpPort: "465",
  };
  const cambiaProtocollo = (p) => {
    setProtocollo(p);
    setInPort(p === "pop3" ? "995" : "993");
    if (!inHost || /^(imap|pop)\./.test(inHost)) setInHost((p === "pop3" ? "pop." : "imap.") + dominio);
  };

  useEffect(() => {
    if (open) {
      setProvider(PROVIDERS.find((p) => p.id === preselect) || null);
      setEmail("");
      setNome("");
      setPassword("");
      setMostraPassword(false);
      setIsPec(false);
      setConnecting(false);
      setErrore("");
      setManuale(false);
      setProtocollo("imap");
      setInHost(""); setInPort(""); setSmtpHost(""); setSmtpPort("");
    }
  }, [open, preselect]);

  const scegli = (p) => {
    setProvider(p);
    setEmail("");
    setErrore("");
    setIsPec(p.id === "aruba" || p.id === "legalmail" || p.id === "namirial");
    setManuale(p.id === "altro");
  };

  const aggiungi = async () => {
    const e = email.trim();
    if (!e || !e.includes("@") || e.startsWith("@")) {
      setErrore("Inserisci un indirizzo email completo (es. nome@gmail.com)");
      return;
    }
    if (!password.trim()) {
      setErrore("Inserisci la password");
      return;
    }
    const extra = {};
    if (manuale) {
      const pIn = parseInt(inPort || suggeriti.inPort, 10);
      const pOut = parseInt(smtpPort || suggeriti.smtpPort, 10);
      if (!(pIn >= 1 && pIn <= 65535) || !(pOut >= 1 && pOut <= 65535)) {
        setErrore("Le porte devono essere numeri tra 1 e 65535");
        return;
      }
      extra.protocollo = protocollo;
      extra.imap_host = (inHost || suggeriti.inHost).trim();
      extra.imap_port = pIn;
      extra.smtp_host = (smtpHost || suggeriti.smtpHost).trim();
      extra.smtp_port = pOut;
    }
    setErrore("");
    setConnecting(true);
    try {
      const acc = await api.addAccount({
        email: e,
        password,
        provider: provider.id,
        provider_nome: provider.nome,
        is_pec: isPec,
        label: nome.trim(),
        ...extra,
      });
      toast.success(`Collegamento a ${acc.email} riuscito`);
      onAdd(acc);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Collegamento non riuscito: controlla la connessione";
      setErrore(msg);
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div className={`wf-modal-backdrop ${open ? "show" : ""}`} onClick={onClose}>
      <div className="wf-modal" onClick={(ev) => ev.stopPropagation()} data-testid="add-account-modal">
        <div className="wf-modal-head">
          <span className="wf-modal-title">
            {provider ? (
              <button
                className="icon-btn"
                style={{ width: 34, height: 34, minWidth: 34 }}
                onClick={() => { setProvider(null); setErrore(""); }}
                aria-label="Indietro"
                data-testid="add-account-back"
              >
                <ArrowLeft size={18} strokeWidth={2.6} />
              </button>
            ) : (
              <UserPlus size={19} strokeWidth={2.3} />
            )}
            Aggiungi account
          </span>
          <button className="icon-btn" onClick={onClose} aria-label="Chiudi" data-testid="add-account-close">
            <X size={20} strokeWidth={2.6} />
          </button>
        </div>

        {!provider ? (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink-soft)", marginBottom: 12 }}>
              Scegli il provider di posta:
            </div>
            <div className="provider-grid">
              {PROVIDERS.map((p) => (
                <button
                  key={p.id}
                  className="provider-btn"
                  onClick={() => scegli(p)}
                  data-testid={`provider-${p.id}`}
                >
                  <span className="provider-badge" style={{ background: p.colore }}>
                    {p.lettera}
                  </span>
                  <span className="provider-name">{p.nome}</span>
                </button>
              ))}
            </div>
          </>
        ) : provider.social ? (
          <>
            <div className="provider-selected">
              <span className="provider-badge" style={{ background: provider.colore }}>
                {provider.lettera}
              </span>
              <span style={{ fontWeight: 800, fontSize: 15 }}>{provider.nome}</span>
            </div>
            <SocialAccountForm provider={provider} onAdd={onAdd} />
          </>
        ) : (
          <>
            <div className="provider-selected">
              <span className="provider-badge" style={{ background: provider.colore }}>
                {provider.lettera}
              </span>
              <span style={{ fontWeight: 800, fontSize: 15 }}>{provider.nome}</span>
            </div>

            <div className="wf-field">
              <label className="wf-label">Indirizzo email</label>
              <input
                className="wf-input"
                placeholder="nome@dominio.it"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErrore(""); }}
                autoFocus
                data-testid="add-account-email"
              />
            </div>
            <div className="wf-field">
              <label className="wf-label">Nome della casella (come vuoi vederla)</label>
              <input
                className="wf-input"
                type="text"
                maxLength={20}
                placeholder="es. Lavoro, Casa, Studio"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                data-testid="add-account-name"
              />
            </div>
            <div className="wf-field">
              <label className="wf-label">Password</label>
              <div className="password-box">
                <input
                  className="wf-input password-input"
                  type={mostraPassword ? "text" : "password"}
                  placeholder="Password dell'account"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setErrore(""); }}
                  data-testid="add-account-password"
                />
                <button
                  className="password-eye"
                  onClick={() => setMostraPassword((v) => !v)}
                  aria-label={mostraPassword ? "Nascondi password" : "Mostra password"}
                  type="button"
                  data-testid="toggle-password"
                >
                  {mostraPassword ? <EyeOff size={19} strokeWidth={2.3} /> : <Eye size={19} strokeWidth={2.3} />}
                </button>
              </div>
            </div>

            <div className="wf-field">
              <label className="pec-check" data-testid="manual-check-label">
                <input
                  type="checkbox"
                  checked={manuale}
                  onChange={(e) => setManuale(e.target.checked)}
                  data-testid="add-account-manual"
                />
                <Settings2 size={15} strokeWidth={2.4} /> Configura manualmente (protocollo, server e porte)
              </label>
            </div>

            {manuale && (
              <div className="manual-box" data-testid="manual-box">
                <div className="wf-field">
                  <label className="wf-label">Protocollo posta in entrata</label>
                  <div className="proto-row">
                    {["imap", "pop3"].map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={`wf-btn proto-btn ${protocollo === p ? "primary" : ""}`}
                        onClick={() => cambiaProtocollo(p)}
                        data-testid={`proto-${p}`}
                      >
                        {p.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="host-port">
                  <div className="wf-field">
                    <label className="wf-label">Server in entrata ({protocollo.toUpperCase()})</label>
                    <input className="wf-input" placeholder={suggeriti.inHost} value={inHost} onChange={(e) => setInHost(e.target.value)} data-testid="manual-in-host" />
                  </div>
                  <div className="wf-field">
                    <label className="wf-label">Porta</label>
                    <input className="wf-input" type="number" min="1" max="65535" placeholder={suggeriti.inPort} value={inPort} onChange={(e) => setInPort(e.target.value)} data-testid="manual-in-port" />
                  </div>
                </div>
                <div className="host-port">
                  <div className="wf-field">
                    <label className="wf-label">Server in uscita (SMTP)</label>
                    <input className="wf-input" placeholder={suggeriti.smtpHost} value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} data-testid="manual-smtp-host" />
                  </div>
                  <div className="wf-field">
                    <label className="wf-label">Porta</label>
                    <input className="wf-input" type="number" min="1" max="65535" placeholder={suggeriti.smtpPort} value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} data-testid="manual-smtp-port" />
                  </div>
                </div>
                <div className="ai-note" style={{ marginTop: 0 }}>
                  Sicurezza automatica: 993/995/465 = SSL, 143/110/587 = STARTTLS.
                  {protocollo === "pop3" && " Con POP3 la posta resta sul server; niente cartelle Inviate/Bozze remote."}
                </div>
              </div>
            )}

            {errore && (
              <div className="connect-error" data-testid="add-account-error">
                <AlertTriangle size={18} strokeWidth={2.4} />
                <span>{errore}</span>
              </div>
            )}

            <div className="wf-field">
              <label className="pec-check" data-testid="pec-check-label">
                <input
                  type="checkbox"
                  checked={isPec}
                  onChange={(e) => setIsPec(e.target.checked)}
                  data-testid="add-account-ispec"
                />
                Account PEC (posta elettronica certificata)
              </label>
            </div>

            <div className="wf-modal-actions">
              <button className="wf-btn primary" onClick={aggiungi} disabled={connecting} data-testid="add-account-submit">
                {connecting ? (
                  <>
                    <Loader2 size={16} strokeWidth={2.6} className="spin-icon" /> Collegamento in corso...
                  </>
                ) : (
                  <>
                    <Check size={16} strokeWidth={2.6} /> Collega account
                  </>
                )}
              </button>
            </div>

            {provider.id === "google" ? (
              <div className="ai-note" style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                <KeyRound size={13} style={{ marginTop: 2, flexShrink: 0 }} />
                <span>
                  Per Gmail serve una <b>Password per le app</b> (non quella normale):{" "}
                  <a
                    href="https://myaccount.google.com/apppasswords"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "inherit", fontWeight: 800, textDecoration: "underline" }}
                    data-testid="gmail-app-password-link"
                  >
                    crea la password per le app qui
                  </a>{" "}
                  (richiede la Verifica in due passaggi attiva).
                </span>
              </div>
            ) : (
              <div className="ai-note" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Mail size={13} /> La connessione ai server di posta viene verificata subito.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
